/*
 * svnty-counted-icon.js - THE COUNTED ICON (R108, D2-28, 2026-09-20). Classic deferred asset, no deps, ASCII.
 *
 * The element is PERSISTENT: it lives outside #cart-icon-bubble (the region Trade re-renders after every add) and
 * reads the TRUTH the theme already maintains - `#cart-icon-bubble [data-svnty-cart-count]` (server-rendered, re-rendered
 * by sections/cart-icon-bubble.liquid, painted by svnty-cart-toast.js) and `[data-svnty-wishlist-count]` (written by
 * svnty-wishlist.js from localStorage). One MutationObserver per truth; nothing else changes. So the glyph never
 * unmounts mid-animation (research COUNTED_ICON section 2: `bag.innerHTML = inner` was the animation killer).
 * Motion (D6-11 THE SYNC BOUNCE, R202): the fill (200 ms), the number's tick (150 ms, in the direction of the change) and
 * THE POP (a rise) or THE SETTLE (a fall) are played by the one conductor, window.SVNTY.pulse (assets/svnty-pulse.js),
 * on one stamp shared with every other glyph of the same change (the card's bag with the header's bag, the card's
 * heart with the header's heart, the menu bar's marks with the header's); reduced motion = the fill and the tick as fades. bfcache: the last value is kept in sessionStorage and reconciled on pageshow
 * (Horizon's trick). Announce: one debounced aria-live sentence.
 * BOUND MODE (D6-7, R200): <svnty-counted-icon data-bound data-svnty-count="N"> takes its truth from its OWN attribute,
 * written by its host (every card's bag, from window.SVNTY.cart in assets/svnty-quickadd.js) and observed; the same
 * fill, number, tick and bump as the header's; no sessionStorage, no live region (the header's icon is the one voice).
 */
(function () {
  'use strict';
  if (!('customElements' in window)) return;
  var TRUTH = { bag: '#cart-icon-bubble [data-svnty-cart-count]', heart: '[data-svnty-wishlist-count]' };

  function readTruth(el) {
    if (!el) return 0;
    if (el.hasAttribute('hidden')) return 0;
    var n = parseInt((el.textContent || '').replace(/\D/g, ''), 10);
    return isNaN(n) ? 0 : n;
  }

  function CountedIcon() { return Reflect.construct(HTMLElement, [], CountedIcon); }
  CountedIcon.prototype = Object.create(HTMLElement.prototype);
  CountedIcon.prototype.constructor = CountedIcon;

  CountedIcon.prototype.connectedCallback = function () {
    var self = this;
    this._digits = this.querySelector('.svnty-ci__digits');
    this._badge = this.querySelector('.svnty-ci__badge');
    this._live = this.querySelector('[data-ci-live]');
    this._kind = this.getAttribute('data-kind') || 'bag';
    if (this.hasAttribute('data-bound')) { this._bind(); return; }
    this._sel = TRUTH[this._kind] || TRUTH.bag;
    this._sync = function (silent) { self.setCount(readTruth(document.querySelector(self._sel)), silent === true); };
    /* first paint: sessionStorage (bfcache / instant) then the truth */
    try {
      var s = JSON.parse(sessionStorage.getItem('svnty:ci:' + this._kind) || 'null');
      if (s && typeof s.v === 'number') this.setCount(s.v, true);
    } catch (e) {}
    /* D6-15 (refuter G-09): the connect's first read of the truth paints still (THE SYNC BOUNCE's law: a page load never
       animates; the menu bar's bag played THE POP on a plain reload when the session's last value was stale); only a
       change after connect moves */
    this._sync(true);
    this._observe();
    /* a page restored from the bfcache paints still (D6-11: only a change the shopper makes moves) */
    window.addEventListener('pageshow', function (e) { if (e.persisted) { self._sync(true); self._takeBus = true; } });
    /* D6-7: a page restored from the bfcache shows the bubble it left with; the cart bus asks /cart.js on that pageshow
       and its answer is the bag's count until the next add re-renders the bubble.
       D6-11 THE BAG PAIR: a real change on the bus (not quiet) is the header's count in the SAME task as the card's bag
       learns it, so the pair's first frame is one frame; the bubble's later repaint then meets the equality return */
    if (this._kind === 'bag') document.addEventListener('svnty:cart', function (e) {
      var d = e.detail;
      if (!d || typeof d.count !== 'number') return;
      if (d.quiet && !self._takeBus) return;
      var still = !!d.quiet || self._takeBus;
      self._takeBus = false; self.setCount(d.count, still);
    });
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      try { subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(self._sync, 0); }); } catch (e) {}
    }
    document.addEventListener('shopify:cart:lines-update', function () { setTimeout(self._sync, 0); });
    if (this._kind === 'heart') document.addEventListener('svnty:wishlist:change', function () { setTimeout(self._sync, 0); });
  };

  CountedIcon.prototype._bind = function () {
    var self = this;
    this._bound = true;
    this._sync = function () { var q = !!self.__svntyQuiet; self.__svntyQuiet = false; self.setCount(parseInt(self.getAttribute('data-svnty-count'), 10) || 0, q); };
    this.setCount(parseInt(this.getAttribute('data-svnty-count'), 10) || 0, true);
    if (this._mo || typeof MutationObserver !== 'function') return;
    this._mo = new MutationObserver(function () { self._sync(); });
    this._mo.observe(this, { attributes: true, attributeFilter: ['data-svnty-count'] });
  };

  /* D6-11: a host's "paint this one still" (__svntyQuiet, set by assets/svnty-quickadd.js before it writes the count)
     lives for the task that set it: the observer reads it in that task's microtask checkpoint, a timer drops it after.
     A quiet paint that changed nothing (the boot's count was already 0) so never silences the shopper's first add. */
  Object.defineProperty(CountedIcon.prototype, '__svntyQuiet', {
    configurable: true,
    get: function () { return !!this._quiet; },
    set: function (v) {
      var self = this;
      this._quiet = !!v;
      if (v) setTimeout(function () { self._quiet = false; }, 0);
    }
  });

  CountedIcon.prototype._observe = function () {
    var self = this;
    if (typeof MutationObserver !== 'function') return;
    var host = this._kind === 'bag' ? document.getElementById('cart-icon-bubble') : document.querySelector(this._sel);
    if (!host) return;
    if (this._mo) this._mo.disconnect();
    this._mo = new MutationObserver(function () { self._sync(); });
    this._mo.observe(host, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
  };

  /* the kinds whose host fades out at zero (the results bar's filter and sort): the last digit stays while it fades */
  var KEEP_AT_ZERO = { filter: true, sort: true };

  CountedIcon.prototype.setCount = function (n, silent) {
    var v = Math.max(0, n | 0);
    var prev = parseInt(this.getAttribute('data-count'), 10) || 0;
    var keep = v === 0 && KEEP_AT_ZERO[this._kind] === true;
    var label = v > 99 ? '99+' : String(v);
    var text = v ? label : (keep ? this._digits.textContent : '');
    var self = this;
    if (v === prev && this._digits.textContent === text) return;
    if (!keep) this.setAttribute('data-digits', String(label.length));
    this._digits.textContent = text;
    if (this._badge) this._badge.textContent = text;
    this.setAttribute('data-count', String(v));
    if (!silent && v !== prev) {
      /* D6-11: the conductor plays the fill, the tick and the spring on the change's one stamp */
      if (window.SVNTY && SVNTY.pulse) {
        SVNTY.pulse.play(this, this._kind, { up: v > prev, fill: prev === 0 && v > 0 ? 'in' : (v === 0 ? 'out' : null), tick: !keep });
      }
      clearTimeout(this._a);
      this._a = setTimeout(function () {
        if (!self._live) return;
        var w = (window.svntyStrings && window.svntyStrings.counted) || {};
        var tpl = self._kind === 'bag' ? (w.bag || '[n] in your bag') : (w.heart || '[n] saved');
        self._live.textContent = tpl.replace('[n]', String(v));
      }, 500);
    }
    if (this._bound) return;
    try { sessionStorage.setItem('svnty:ci:' + this._kind, JSON.stringify({ v: v, t: Date.now() })); } catch (e) {}
  };

  if (!customElements.get('svnty-counted-icon')) customElements.define('svnty-counted-icon', CountedIcon);
})();
